/**
 * Request information about the current user
 */

import { message as antdMessage } from 'antd';
import axios, { AxiosError, AxiosResponse } from 'axios';
import parsedPhoneNumber from 'libphonenumber-js';
import get from 'lodash/get';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';

import { MESSAGES } from 'clients/utils/parse-axios-error';
import User from 'types/models/user';
import { UserPermission } from 'types/models/user-permission';
import { EntityKind } from 'types/models/user-preference';
import { checkIsInternalAdmin } from 'utils/AccessControl/check-is-internal-admin';
import { parseInternalAccessPolicy } from 'utils/AccessControl/parse-internal-access-policy';
import parseUserPhones from 'utils/phone-number/parse-user-phones';
import { getRequest, patchRequest } from 'utils/redux-saga-requests';

import {
  editMyUser,
  editMyUserFailure,
  editMyUserSuccess,
  loadMyUser,
  loadMyUserFailure,
  loadMyUserSuccess,
  mergeMyUser
} from './actions';
import ActionTypes from './constants';
import { selectMyUser } from './selectors';

export function* requestMyUser(action?: ReturnType<typeof loadMyUser>) {
  const user: User = yield select(selectMyUser);
  try {
    const response: AxiosResponse = yield call(getRequest, `user/${user.id}`, {
      params: {
        join: [
          'memberships',
          'memberships.enterprise',
          'memberships.enterprise.billingAddress',
          'memberships.enterprise.billingAddress.state',
          'memberships.enterprise.billingAddress.state.country',
          'memberships.enterprise.enterpriseIntegration'
        ],
        sort: 'memberships.enterprise.name,ASC'
      }
    });
    const { data } = response;
    let myUser = parseUserPhones(data);

    if (checkIsInternalAdmin(myUser)) {
      // Fetch admin policy if myUser is an internal admin.
      const { data: adminPolicyData }: AxiosResponse = yield call(getRequest, 'admin-policy');

      // Fetch ip-camera whitelisted access if myUser is an internal admin.
      // For privacy reasons, internal admins by default can see IPCameraMapFlag on MapView but cannot view any
      // content on IPCameraListPage (where the thumbnails are displayed) or IPCameraDetailsPage.
      // This protects property owners' privacy.
      const { data: ipCameraPermissions }: AxiosResponse<UserPermission[]> = yield call(getRequest, 'user-permissions', {
        params: {
          entityKind: EntityKind.IP_CAMERA
        }
      });

      myUser = parseInternalAccessPolicy(myUser, adminPolicyData, ipCameraPermissions);
    }

    yield all([put(mergeMyUser(myUser)), put(loadMyUserSuccess(response))]);
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const message = error.message === MESSAGES.NETWORK_ERROR
      ? error.message
      : get(error, 'response.data.message', 'We could not load your user profile. Please sign in again');
    yield put(loadMyUserFailure(message, error));
  }
}

export function* watchLoadMyUserRequest() {
  yield takeLatest(ActionTypes.LOAD_MY_USER_REQUEST, requestMyUser);
}

export function* requestEditMyUser(action: ReturnType<typeof editMyUser>) {
  const user: User = yield select(selectMyUser);

  const {
    payload: {
      title,
      firstName,
      lastName,
      username,
      mobileNumberCountryCode,
      mobileNumber,
      landlineNumberCountryCode,
      landlineNumber,
      enableSmsNotifications,
      email,
      enableEmailNotifications
    }
  } = action;
  try {
    const mobileNumberValue = {
      mobileNumber:
        mobileNumber && mobileNumberCountryCode
          ? parsedPhoneNumber(`${mobileNumberCountryCode}${mobileNumber}`)
            ?.number || null
          : null
    };
    const landlineNumberValue = {
      landlineNumber:
        landlineNumber && landlineNumberCountryCode
          ? parsedPhoneNumber(`${landlineNumberCountryCode}${landlineNumber}`)
            ?.number || null
          : null
    };
    const response: AxiosResponse = yield call(
      patchRequest,
      `user/${user.id}`,
      {
        title,
        firstName,
        lastName,
        username,
        mobileNumberCountryCode: mobileNumberCountryCode || null,
        // Pass phone number in the format needed by backend.
        ...mobileNumberValue,
        landlineNumberCountryCode: landlineNumberCountryCode || null,
        // Pass phone number in the format needed by backend.
        ...landlineNumberValue,
        email: email || null,
        enableEmailNotifications,
        enableSmsNotifications
      }
    );
    const { data } = response;
    yield all([
      put(
        mergeMyUser(parseUserPhones(data))
      ),
      put(editMyUserSuccess(response))
    ]);

    antdMessage.success('Profile settings updated');
  } catch (error) {
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    );
    yield put(editMyUserFailure(message, error as AxiosError));
  }
}

export function* watchEditMyUserRequest() {
  yield takeLatest(ActionTypes.EDIT_MY_USER_REQUEST, requestEditMyUser);
}

export default function* myUserSaga() {
  yield all([fork(watchLoadMyUserRequest), fork(watchEditMyUserRequest)]);
}
